/// <reference types="vite/client" />
/** Where the service worker serves project files: under the site's base path (set with Vite's `base` for a subfolder). */
export const FILES_PATH = `${import.meta.env.BASE_URL}files/`;

/**
 * Where the service worker serves a project as the game sees it under Vite:
 * a path is looked up in the served root (Vite's public folder, such as
 * 'public/') first, then from project.json's folder. The player runs game
 * code with its document base here, so the game's relative URLs work.
 */
export const GAME_PATH = `${import.meta.env.BASE_URL}game/`;

export function gameBase(id: string): string {
  return `${GAME_PATH}${encodeURIComponent(id)}/`;
}

/** The project's `root` folder as a path prefix: '' or 'public/'. */
export function rootPrefix(projectJson: unknown): string {
  const root = String((projectJson as { root?: string } | undefined)?.root ?? '.').replace(/^\.\/?/, '').replace(/\/$/, '');
  return root ? `${root}/` : '';
}

/** URL that a project's files are served under, ending in /. `root` is the project's root prefix, such as 'public/'. */
export function filesBase(id: string, root: string): string {
  return `${FILES_PATH}${encodeURIComponent(id)}/${root}`;
}

const TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  json: 'application/json',
  mp3: 'audio/mpeg',
  ogg: 'audio/ogg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  flac: 'audio/flac',
  txt: 'text/plain; charset=utf-8',
  html: 'text/html; charset=utf-8',
  css: 'text/css; charset=utf-8',
  js: 'text/javascript; charset=utf-8',
  mp4: 'video/mp4',
  webm: 'video/webm',
  ogv: 'video/ogg',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  woff: 'font/woff',
  woff2: 'font/woff2',
  ttf: 'font/ttf',
  otf: 'font/otf',
  xml: 'application/xml',
  fnt: 'text/plain; charset=utf-8',
  glsl: 'text/plain; charset=utf-8',
  frag: 'text/plain; charset=utf-8',
};

export function contentType(path: string): string {
  return TYPES[path.split('.').pop()?.toLowerCase() ?? ''] ?? 'application/octet-stream';
}

/** Registers the service worker and resolves once it controls this page. */
export async function ensureServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) throw new Error('This browser has no service workers, which the browser version needs to load project files.');
  await navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`, { scope: import.meta.env.BASE_URL });
  await navigator.serviceWorker.ready;
  if (navigator.serviceWorker.controller) return;
  // The first visit: wait for the new worker to claim this page.
  await new Promise<void>((resolve) => navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true }));
}
